import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faEdit,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Modal,
  Badge,
} from "@themesberg/react-bootstrap";
import {
  adminCreateBonusSlab,
  adminGetBonusSlabs,
  adminGetBonusSlabById,
  adminUpdateBonusSlab,
} from "../../../pages/api";
import { useNavigate } from "react-router-dom";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const mapBonusSlab = (item) => ({
  id: item?.id ?? Date.now(),
  slab_name: item?.slab_name || "",
  threshold: item?.threshold ?? "",
  amount: item?.amount ?? "",
  gift_description: item?.gift_description || "",
  status: item?.status || "active",
  created_at: item?.created_at || "",
});

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const badgeVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (["active", "processed", "sent", "eligible", "ready to process"].includes(s)) return "success";
  if (["pending", "draft"].includes(s)) return "warning";
  if (["inactive", "failed", "not eligible", "already claimed", "archived"].includes(s)) return "danger";
  return "secondary";
};

const formatDateTime = (value) => {
  if (!value) return "N/A" || "-";

  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function AdminBonusSlabSetup() {
  const navigate = useNavigate();
  const [slabs, setSlabs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSlab, setEditingSlab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetchingEdit, setFetchingEdit] = useState(false);
  const [formData, setFormData] = useState({
    slab_name: "",
    threshold: "",
    amount: "",
    gift_description: "",
    status: "active",
  });
  const [errors, setErrors] = useState({
    slab_name: "",
    threshold: "",
    amount: "",
    status: "",
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const fetchBonusSlabs = async (page = 1, perPage = 10) => {
    try {
      setLoading(true);

      const response = await adminGetBonusSlabs({
        page,
        per_page: perPage,
      });

      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiSlabs = Array.isArray(apiData?.slabs) ? apiData.slabs : [];
      const apiPagination = apiData?.pagination || {};

      setSlabs(apiSlabs.length > 0 ? apiSlabs.map(mapBonusSlab) : []);
      setPagination({
        current_page: apiPagination.current_page ?? 1,
        last_page: apiPagination.last_page ?? 1,
        per_page: apiPagination.per_page ?? 10,
        total: apiPagination.total ?? apiSlabs.length,
      });
    } catch (error) {
      console.error("Failed to fetch bonus slabs:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch bonus slabs.");
      setSlabs([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonusSlabs(1, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getShowingRange = () => {
    const total = Number(pagination.total || 0);
    const current = Number(pagination.current_page || 1);
    const perPage = Number(pagination.per_page || 10);

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    let start = (current - 1) * perPage + 1;
    let end = Math.min(current * perPage, total);

    if (start > total) {
      start = total;
      end = total;
    }

    if (end < start) {
      end = start;
    }

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start: showingStart, end: showingEnd } = useMemo(() => getShowingRange(), [
    pagination.current_page,
    pagination.last_page,
    pagination.per_page,
    pagination.total,
  ]);

  const openAddModal = () => {
    setEditingSlab(null);
    setFormData({
      slab_name: "",
      threshold: "",
      amount: "",
      gift_description: "",
      status: "active",
    });
    setErrors({
      slab_name: "",
      threshold: "",
      amount: "",
      status: "",
    });
    setShowModal(true);
  };

  const openEditModal = async (slab) => {
    if (!slab?.id) {
      toast.error("Invalid slab selected.");
      return;
    }

    try {
      setFetchingEdit(true);

      const response = await adminGetBonusSlabById(slab.id);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};

      const selectedSlab = mapBonusSlab(apiData);

      setEditingSlab(selectedSlab);
      setFormData({
        slab_name: selectedSlab?.slab_name || "",
        amount: selectedSlab?.amount ?? "",
        threshold: selectedSlab?.threshold ?? "",
        gift_description: selectedSlab?.gift_description || "",
        status: selectedSlab?.status || "active",
      });
      setErrors({
        slab_name: "",
        amount: "",
        threshold: "",
        status: "",
      });
      setShowModal(true);
    } catch (error) {
      console.error("Failed to fetch bonus slab by id:", error);
      toast.error(error?.response?.data?.message || "Unable to load slab details.");
    } finally {
      setFetchingEdit(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSlab(null);
    setFormData({
      slab_name: "",
      threshold: "",
      amount: "",
      gift_description: "",
      status: "active",
    });
    setErrors({
      slab_name: "",
      threshold: "",
      amount: "",
      status: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "threshold" && value !== "" && !/^\d*$/.test(value)) {
      return;
    }

    if (name === "amount" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "slab_name" || name === "threshold" || name === "status" || name === "amount") {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const slabName = String(formData.slab_name || "").trim();
    const threshold = String(formData.threshold || "").trim();
    const status = String(formData.status || "").trim();
    const amount = String(formData.amount || "").trim();
    const giftDescription = String(formData.gift_description || "").trim();

    const newErrors = {
      slab_name: !slabName ? "Slab Name is required" : "",
      threshold: !threshold ? "Threshold is required" : "",
      status: !status ? "Status is required" : "",
      amount: !amount ? "Amount is required" : "",
    };

    setErrors(newErrors);

    if (newErrors.slab_name || newErrors.threshold || newErrors.status || newErrors.amount) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        slab_name: slabName,
        threshold: threshold,
        gift_description: giftDescription,
        status,
        amount,
      };

      const response = editingSlab
        ? await adminUpdateBonusSlab(editingSlab.id, payload)
        : await adminCreateBonusSlab(payload);

      toast.success(response?.data?.message || `Bonus slab ${editingSlab ? "updated" : "created"} successfully.`);
      closeModal();
      await fetchBonusSlabs(pagination.current_page, pagination.per_page);
    } catch (error) {
      console.error("Bonus slab save failed:", error);
      toast.error(error?.response?.data?.message || "Unable to save bonus slab.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = async (page) => {
    if (page === pagination.current_page || loading) return;
    await fetchBonusSlabs(page, pagination.per_page);
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4 gap-2">
        <div>
          <h2 className="h4 mb-1">Bonus Slab Setup</h2>
          {/* <div className="text-muted">Define slab name, threshold, gift description, and status.</div> */}
        </div>

        <div className="d-flex justify-content-end ms-auto">
          <Button type="button" onClick={openAddModal} style={ACTIVE_BUTTON_STYLE} className="fw-semibold me-2">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Bonus Slab
          </Button>

          <Button
            type="button"
            variant="outline-primary"
            onClick={() => navigate(-1)}
            className="admin-back-btn"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
        </div>
      </div>

      <Row className="g-4">
        <Col xs={12} md={12}>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <h5 className="mb-1">Bonus Slab List</h5>
                  {/* <div className="text-muted small">Manage bonus slabs from here.</div> */}
                </div>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Slab Name</th>
                      <th>Threshold</th>
                      <th>Amount</th>
                      <th>Gift Description</th>
                      <th>Status</th>
                      {/* <th>Created At</th> */}
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          Loading bonus slabs...
                        </td>
                      </tr>
                    ) : slabs.length > 0 ? (
                      slabs.map((item, index) => (
                        <tr key={item.id}>
                          <td className="fw-semibold">{showingStart + index}</td>
                          <td>{item.slab_name}</td>
                          <td>{item.threshold}</td>
                          <td>{item.amount}</td>
                          <td>{item.gift_description || "N/A" || "-"}</td>
                          <td>
                            <Badge bg={badgeVariant(item.status)} className="rounded-pill px-3 py-2">
                              {capitalize(item.status)}
                            </Badge>
                          </td>
                          {/* <td>{formatDateTime(item.created_at)}</td> */}
                          <td className="text-center">
                            <Button
                              type="button"
                              variant="link"
                              className="p-0 text-decoration-none"
                              onClick={() => openEditModal(item)}
                              disabled={fetchingEdit}
                            >
                              <FontAwesomeIcon icon={faEdit} size="sm" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          No bonus slabs found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted small">
                  Showing {showingStart} to {showingEnd} of {pagination.total} entries
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={pagination.current_page <= 1 || loading}
                    onClick={() => {
                      handlePageChange(Math.max(1, pagination.current_page - 1));
                    }}
                    className="course-page-btn"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Button>

                  {getPageItems().map((item, index) =>
                    item === "..." ? (
                      <span key={`dots-${index}`} className="text-muted px-1">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={item}
                        type="button"
                        variant="outline-primary"
                        disabled={loading}
                        onClick={() => {
                          handlePageChange(item);
                        }}
                        className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                      >
                        {item}
                      </Button>
                    )
                  )}

                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={pagination.current_page >= pagination.last_page || loading}
                    onClick={() => {
                      handlePageChange(Math.min(pagination.last_page, pagination.current_page + 1));
                    }}
                    className="course-page-btn"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        dialogClassName="bonus-slab-modal"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingSlab ? "Edit Bonus Slab" : "Add Bonus Slab"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {fetchingEdit ? (
              <div className="text-center text-muted py-4">
                Loading slab details...
              </div>
            ) : (
              <>
                <Form.Group className="mb-3" controlId="slabName">
                  <Form.Label className="fw-semibold">
                    Slab Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="slab_name"
                    type="text"
                    placeholder="Enter slab name"
                    value={formData.slab_name}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.slab_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.slab_name}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="threshold">
                  <Form.Label className="fw-semibold">
                    Threshold <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="threshold"
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter threshold"
                    value={formData.threshold}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.threshold}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.threshold}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="amount">
                  <Form.Label className="fw-semibold">
                    Amount <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="amount"
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter amount"
                    value={formData.amount}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.amount}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amount}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-0" controlId="status">
                  <Form.Label className="fw-semibold">
                    Status <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                    isInvalid={!!errors.status}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.status}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="giftDescription">
                  <Form.Label className="fw-semibold">Gift Description</Form.Label>
                  <Form.Control
                    name="gift_description"
                    as="textarea"
                    rows={4}
                    placeholder="Enter gift description"
                    value={formData.gift_description}
                    onChange={handleChange}
                    style={{ boxShadow: "none" }}
                  />
                </Form.Group>                
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={closeModal} disabled={submitting || fetchingEdit}>
              Cancel
            </Button>
            <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting || fetchingEdit}>
              {submitting ? "Saving..." : editingSlab ? "Update Bonus Slab" : "Add Bonus Slab"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style>{`
        .modal-dialog.bonus-slab-modal {
          width: min(95vw, 70rem) !important;
          max-width: 50rem !important;
          margin: 1.75rem auto !important;
        }

        @media (max-width: 767.98px) {
          .modal-dialog.bonus-slab-modal {
            width: min(96vw, 36rem) !important;
            max-width: 36rem !important;
            margin: 1rem auto !important;
          }
        }
      `}</style>
    </>
  );
}